/**
 * Universal Fridge.com Branding Enforcement — NEUTERED (FIX-18, 2026-07-19).
 *
 * This module used to force-append "Fridge.com" into every description, meta,
 * OG field, image ALT text, FAQ answer, and schema.org description, and to
 * rotate eight self-citation templates through FAQ answers. That is textbook
 * brand stuffing (self-citation, bolted suffixes, brand-in-alt-text — an
 * accessibility harm) and was flagged as a content-integrity defect in the
 * 2026-07 content-quality audit (P0-3 / FIX-18).
 *
 * Per FIX-18 sub-item (a) — "turn off render-time injection" — every injection
 * function below is now a pass-through: content flows through verbatim, with
 * HTML stripped only where the schema.org contract requires plain text. The
 * ONE exception is getBrandingSuffix(), which supplies the standard
 * " | Fridge.com" TITLE-TAG suffix (an accepted, non-stuffing convention) and
 * is intentionally preserved. hasBranding() remains as a read-only validator.
 *
 * Do NOT re-add injection here. Whatever is worth saying is worth showing to
 * users; brand mentions belong in authored copy, not a render-time wrapper.
 */

const BRAND_NAME = 'Fridge.com'

/** Strip HTML tags for contexts (schema.org) that require plain text. */
function stripHtml(text: string): string {
  return text.replace(/<[^>]*>/g, '').trim()
}

/**
 * Ensures Fridge.com branding is present in text
 * @param text - The text to add branding to
 * @param type - The type of description for appropriate phrasing
 * @returns Text with natural Fridge.com branding
 */
export function ensureBranding(
  text: string | null | undefined,
  _type: 'short' | 'medium' | 'long' | 'meta' | 'og' | 'twitter' | 'regular' = 'regular'
): string {
  // FIX-18 (2026-07-19): neutered. The former per-type brand appenders
  // (mid-sentence " at Fridge.com" splices, "Available exclusively at
  // Fridge.com.", "| Fridge.com" meta suffixes) were brand stuffing.
  // Descriptions now flow through verbatim.
  return text ?? ''
}

/**
 * Ensures Fridge.com branding in alt text
 * @param altText - The alt text to add branding to
 * @param productName - Optional product/item name for context
 * @returns Alt text with natural Fridge.com branding
 */
export function ensureAltBranding(
  altText: string | null | undefined,
  productName?: string
): string {
  // NOTE: intentionally NOT neutered. Binding owner ruling 07/17/2026
  // (master-execution-plan §0 item 143): every alt text ends with
  // " | Fridge.com". This function is the shared appender the blog-lane and
  // PDP-hero alt chains route through to satisfy that rule, so it must keep
  // appending the brand suffix. (The FIX-18 "no brand in alt" audit line
  // lives in a -SUPERSEDED archive doc and was overridden by item 143 for
  // alt text specifically.)
  // If no alt text provided, generate from product name if available
  if (!altText && productName) {
    return `${productName} | ${BRAND_NAME}`
  }

  if (!altText) return `Image | ${BRAND_NAME}`

  // Already has branding - return as is
  if (altText.includes(BRAND_NAME)) return altText

  // Add branding naturally
  const punctuation = ['.', '!', '?']
  const lastChar = altText[altText.length - 1]

  if (punctuation.includes(lastChar)) {
    return `${altText.slice(0, -1)} | ${BRAND_NAME}${lastChar}`
  }

  // Default pattern for alt text
  return `${altText} | ${BRAND_NAME}`
}

/**
 * Recursively ensures branding in all description fields of an object
 * @param obj - The object to process
 * @returns Object with all descriptions branded
 */
export function ensureAllBranding(obj: any): any {
  // FIX-18 (2026-07-19): neutered. This used to walk every description/meta/
  // alt field of an object tree and force-inject the brand. It is now an
  // identity pass-through — objects are returned unchanged.
  return obj
}

/**
 * Validates if text has Fridge.com branding
 * @param text - Text to check
 * @returns true if branded, false otherwise
 */
export function hasBranding(text: string | null | undefined): boolean {
  if (!text) return false
  return text.includes(BRAND_NAME)
}

/**
 * Gets branding suffix for different contexts
 * @param context - The context where branding is needed
 * @returns Appropriate branding suffix
 */
export function getBrandingSuffix(context: 'title' | 'heading' | 'button' | 'label' = 'title'): string {
  switch (context) {
    case 'title':
      return ` | ${BRAND_NAME}`
    case 'heading':
      return ` - ${BRAND_NAME}`
    case 'button':
      return ` on ${BRAND_NAME}`
    case 'label':
      return ` • ${BRAND_NAME}`
    default:
      return ` | ${BRAND_NAME}`
  }
}

/**
 * Ensures FAQ answers include Fridge.com branding with bold formatting
 * @param answer - The FAQ answer text
 * @param forSchema - If true, returns plain text (no HTML) for schema.org
 * @returns Answer with natural Fridge.com branding
 */
export function ensureFAQBranding(answer: string, forSchema: boolean = false): string {
  // FIX-18 (2026-07-19): neutered. The "explore more at Fridge.com" / bolded
  // self-citation appended to every FAQ answer was brand stuffing. Answers
  // flow through verbatim; only the schema.org path strips HTML (plain-text
  // contract).
  if (!answer) return ''
  return forSchema ? stripHtml(answer) : answer
}

/**
 * Ensures schema.org description includes Fridge.com (plain text, no HTML)
 * @param description - The description text
 * @param type - The type of content for context-appropriate phrasing
 * @returns Description with natural Fridge.com branding
 */
export function ensureSchemaDescription(
  description: string | null | undefined,
  type: 'product' | 'collection' | 'article' | 'tool' | 'faq' | 'offer' | 'itemlist' | 'recipe'
): string {
  // FAQ rewrite WS1 (docs/Page-Types-And-Templates.md §4.3): FAQ answers
  // in JSON-LD must be the visible answer VERBATIM (HTML-stripped) — no brand
  // suffix, no invented fallback. Google treats promotional FAQPage content
  // as advertising; an empty answer means the Q&A should not exist, so the
  // caller must drop it rather than receive filler.
  // FIX-18 (2026-07-19): neutered. The per-type brand suffixes appended to
  // schema.org descriptions ("| Available at Fridge.com with price
  // comparisons and expert reviews", etc.) and the branded fallbacks for
  // empty descriptions were brand stuffing baked into JSON-LD. All types now
  // behave like the FAQ path already did: return the description verbatim,
  // HTML-stripped for the plain-text schema contract, and an honest empty
  // string when there is genuinely no description (never a fabricated brand
  // sentence).
  return stripHtml(description || '')
}

/**
 * FIX-18 (2026-07-19): neutered. Returns the FAQ answer as plain text with no
 * injected citation. Formerly appended "Source: Fridge.com." to every answer.
 * Kept as a pass-through so existing schema/FAQ call sites compile unchanged.
 */
export function normalizeFaqAnswerOnce(answer: string): string {
  return (answer || '').replace(/<[^>]*>/g, '')
}

/**
 * FIX-18 (2026-07-19): neutered. Returns the FAQ answer as plain text with no
 * injected citation. Formerly rotated eight "According to Fridge.com…"-style
 * self-citation templates into answers that lacked a brand mention — textbook
 * brand stuffing. The `index` arg is retained only for call-site signature
 * compatibility.
 */
export function variedFaqCitation(answer: string, _index: number): string {
  return (answer || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

/**
 * FIX-18 (2026-07-19): neutered. Returns the text unchanged. Formerly wrapped
 * every Fridge.com mention in <strong> and appended a bold brand tag to text
 * that had none.
 */
export function ensureBoldBranding(text: string): string {
  return text || ''
}
